import lineClip from 'lineclip';

import {gmAPI, MapService} from '../../MapService';
import {createSVGElement, makeDAttribute, setSVGOverlayViewOptions, isClosedPath, MAX_SVG_VERTICES} from './svgOverlayHelpers';
import {pSBC, standardizeColor} from '../colorUtilities.js';
import {addDomMutation} from '@/services/GraphicService/Graphic.js';

/** @typedef {import('../../../lib/geometry/simplify').Point} PointXY */

/** @type {Record<string, string>} Maps Google Maps API poly options to corresponding SVG styles */
const polyToSVGAttributes = {
  'strokeColor': 'stroke',
  'strokeOpacity': 'stroke-opacity',
  'fillColor': 'fill',
  'fillOpacity': 'fill-opacity',
  'strokeWeight': 'stroke-width',
};

/** @type {google.maps.PolygonOptions} */
const defaultOptions = {
  'strokeColor': '#f00',
  'strokeOpacity': 1,
  'strokeWeight': 4,
  'fillColor': '#f00',
  'fillOpacity': 0.5,
};

/** The number of decimal degrees for expanding the calculated viewport at each zoom level */
const degreesAtZoom = [
  5.547, 2.773, 1.387, 0.693, 0.347, 0.173, 0.0867, 0.0433, 0.0217, 0.0108, 0.0054, 0.0027, 0.00135, 0.000678,
  0.00034, 0.00017, 0.00017, 0.00017, 0.00017, 0.00017, 0.00017, 0.00017,
];

class SVGOverlayView extends gmAPI.OverlayView {
  /**
   * @param {google.maps.PolygonOptions | google.maps.PolylineOptions} options
   * @param {string} id The unique identifier to attach to the DOM element.
   * @param {"polygon"|"polyline"} [svgType='polygon'] Type of svg
   */
  constructor(options = {}, id, svgType = 'polygon') {
    super();
    /** @type {Array<Array<PointXY>>} */
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- JSDoc type declaration
    this.paths;

    /** @type {google.maps.LatLngBounds} */
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- JSDoc type declaration
    this.bounds;

    /** @type {string} */
    this.id = id;

    /** @type {SVGSVGElement} */
    this.displaySvg = createSVGElement();
    /** @type {SVGSVGElement} */
    this.clickSvg = createSVGElement();

    // The ID is ONLY attached to the click SVG to ensure the central click handler can uniquely identify the graphic
    this.clickSvg.id = id;

    /** @type {SVGPathElement[]} */
    this.pathElements = [];
    /** @type {SVGUseElement[]} */
    this.displayUseElements = [];
    /** @type {SVGUseElement[]} */
    this.clickUseElements = [];

    /** @type {"polygon"|"polyline"} */
    this.svgType = svgType;

    /** @type {google.maps.PolygonOptions & google.maps.PolylineOptions} */
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- JSDoc type declaration
    this.options;

    /** @type {{eventName: string, handler: Function}[]} */
    this.listeners = [];

    /**
     * @type {Array<Array<Array<PointXY>>>} 4D array:
     *    Array of paths in the poly ->
     *      Array of a simplified version of each path per zoom level ->
     *        Array of points in the simplified path ->
     *          Array [x, y] of the point's coordinates
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- JSDoc type declaration
    this.simplifiedPaths;

    /** @type {boolean} indicates whether this image has been highlighted */
    this.highlighted = false;

    /** @type {number} Keep track of the last draw width to skip recalculating path points when zoom has not changed. */
    this.lastDrawWidth = 0;

    try {
      setSVGOverlayViewOptions(this, options, polyToSVGAttributes, defaultOptions);
    } catch (e) {
      e.message = `Error creating SVGOverlayView: ${e.message}`;
      throw e;
    }
  }

  async onAdd() {
    const {overlayMouseTarget} = this.getPanes();
    const subPane = (await MapService.getSubPanes()).find((element) => element.id = 'overlay-graphics');
    if (subPane === undefined) {
      console.error(`SVGOverlayView: couldn't find the 'overlay-graphics' subpane to append SVG`);
      return;
    }
    addDomMutation(() => {
      overlayMouseTarget.appendChild(this.clickSvg);
      subPane.appendChild(this.displaySvg);
    });
  }

  onRemove() {
    addDomMutation(() => {
      this.clickSvg.parentNode.removeChild(this.clickSvg);
      this.displaySvg.parentNode.removeChild(this.displaySvg);
    });
  }

  /** @param {boolean} enabled */
  setPointerEvents(enabled = true) {
    const pointerEvents = enabled ? 'auto' : 'none';
    addDomMutation(() => {
      this.pathElements.forEach((pathElement) => pathElement.style.pointerEvents = pointerEvents);
    });
  }

  draw() {
    const projection = this.getProjection();
    if (!projection) return;

    const sw = projection.fromLatLngToDivPixel(this.bounds.getSouthWest());
    const ne = projection.fromLatLngToDivPixel(this.bounds.getNorthEast());
    const width = Math.round(ne.x - sw.x); // round to the nearest pixel to minimize a miss on matching the last width
    const height = sw.y - ne.y;

    // if the svg is tiny or not visible, skip the rest of the calculations
    if ((width < 10 && height < 10)) {
      this.displaySvg.style.display = 'none';
      this.clickSvg.style.display = 'none';
      this.lastDrawWidth = 0;
      return;
    }

    // Reposition and resize the SVG elements
    [this.displaySvg, this.clickSvg].forEach((svg) => {
      // Due to SVG's coordinate system being drawn in "user space", we need to account for the thickness of the stroke
      // and add extra size to the viewBox to ensure that the stroke is not clipped. In order to remain positioned
      // correctly, we also need to offset the svg element.
      if (this.svgType === 'polyline') {
        const stroke = this.options.strokeWeight || 0;
        const halfStroke = stroke / 2;
        const widthStroke = Math.max(width + stroke, 0);
        const heightStroke = Math.max(height + stroke, 0);
        // Execute the position mutations immediately to avoid flickering.
        svg.style.left = `${sw.x - (halfStroke)}px`;
        svg.style.top = `${ne.y - (halfStroke)}px`;
        svg.style.width = `${widthStroke}px`;
        svg.style.height = `${heightStroke}px`;
        // Batch the rest of the mutations for performance.
        addDomMutation(() => {
          svg.style.display = 'block';
          svg.setAttribute('viewBox', `${-(halfStroke)} ${-halfStroke} ${widthStroke} ${heightStroke}`);
        });
      } else {
        svg.style.left = `${sw.x}px`;
        svg.style.top = `${ne.y}px`;
        svg.style.width = `${Math.max(width, 0)}px`;
        svg.style.height = `${Math.max(height, 0)}px`;
        addDomMutation(() => {
          svg.style.display = 'block';
          svg.setAttribute('viewBox', `${0} ${0} ${width} ${height}`);
        });
      }
      // The wrapping <svg> element should not receive clicks so that only the precise path elements respond to events.
      addDomMutation(() => svg.style.pointerEvents = 'none');
    });

    const zoom = Math.round(this.getMap().getZoom());

    // Manipulating the DOM is very expensive. For extremely large SVGs, it is worth the tradeoff of more DOM
    // manipulation to clip vertices that are outside the viewport. However we need to be careful to only make changes
    // to un-clipped polys when the width of the SVG has changed.
    this.simplifiedPaths.forEach((pathsAtZoom, i) => {
      let points = pathsAtZoom[zoom];
      // if the width has not changed and the polygon is not a candidate for clipping, we can skip redrawing the
      // path points
      if (width === this.lastDrawWidth && points.length <= (MAX_SVG_VERTICES)) return;

      // The polyline clip function may return multiple disjoint segments, but we cannot handle segments.
      // until we experience performance issues with polyline rendering, do not clip polyline paths.
      if (this.svgType === 'polygon' && points.length > (MAX_SVG_VERTICES)) {
        const viewBounds = MapService.getViewBounds(projection);
        /** @type {[number, number, number, number]} bBox is in the form [minX, minY, maxX, maxY] */
        const bBox = [
          // extend the bounds a little bit for each zoom level to make sure it is outside the viewport
          viewBounds.getSouthWest().lng() - degreesAtZoom[zoom],
          viewBounds.getSouthWest().lat() - degreesAtZoom[zoom],
          viewBounds.getNorthEast().lng() + degreesAtZoom[zoom],
          viewBounds.getNorthEast().lat() + degreesAtZoom[zoom],
        ];
        points = lineClip.polygon(points, bBox);
      }
      const pixels = points.map((p) => {
        const pixel = projection.fromLatLngToDivPixel({lat: p[1], lng: p[0]});
        return [pixel.x - sw.x, pixel.y - ne.y];
      });
      const dAttr = makeDAttribute(pixels);
      addDomMutation(() => {
        this.pathElements[i].setAttribute('d', dAttr);
      });
    });
    // this must be the very last statement or it will prevent multipolys from rendering anything but the first path
    this.lastDrawWidth = width;
  }

  /**
   * @param {google.maps.PolygonOptions & google.maps.PolylineOptions} options
   */
  setOptions(options) {
    // Preserve any options which are not explicitly overridden
    options = Object.assign({}, this.options, options);
    // if new paths are not provided, use the existing paths
    if (!options.paths && !options.path) {
      options.paths = this.paths;
    }
    try {
      setSVGOverlayViewOptions(this, options, polyToSVGAttributes, defaultOptions);
    } catch (e) {
      throw new Error(`Error updating options on SVGOverlayView: ${e.message}`);
    }
    // Attempting to draw while the map is not yet initialized will throw an error. Options may be changed before the
    // map is initialized, so we need to check if the map is initialized before drawing.
    if (this.map) {
      this.lastDrawWidth = 0;
      this.draw();
    }
  }

  /**
   * @param {boolean} highlighted
   */
  setHighlighted(highlighted) {
    this.highlighted = highlighted;
    this.displayUseElements.forEach((pathElement, i) => {
      const stroke = standardizeColor(this.options.strokeColor);
      const fill = standardizeColor(this.options.fillColor);
      addDomMutation(() => {
        pathElement.style.stroke = highlighted ? pSBC(0.5, stroke, '#fff') : stroke;
        if (isClosedPath(this.paths[i])) {
          pathElement.style.fill = highlighted ? pSBC(0.5, fill, '#fff') : fill;
        }
      });
    });
  }

  /**
   * @param {string} eventName event name such as 'click'
   * @param {Function} handler callback function called when the event is triggered
   * @return {Function} the handler
   */
  addListener(eventName, handler) {
    this.listeners.push({eventName, handler});
    this.clickUseElements.forEach((pathElement) => {
      pathElement.addEventListener(eventName, handler);
    });
    return handler;
  }

  /**
   * @param {string} eventName event name such as 'click'
   * @param {Function} handler callback function called when the event is triggered
   */
  removeListener(eventName, handler) {
    this.listeners = this.listeners.filter((listener) => {
      return listener.eventName !== eventName || listener.handler !== handler;
    });
    this.clickUseElements.forEach((pathElement) => {
      pathElement.removeEventListener(eventName, handler);
    });
  }

  /**
   * @param {number} zIndex
   */
  setZIndex(zIndex) {
    addDomMutation(() => {
      this.clickSvg.style.zIndex = `${zIndex}`;
      this.displaySvg.style.zIndex = `${zIndex}`;
    });
  }
}

export {
  SVGOverlayView,
  defaultOptions,
};
